import { matchPath, useParams } from "react-router-dom"

import { AnalyticStatusConfig } from "./AnalyticStatusConfig"

import { APP_STATUSES } from "@utils/AppStatusContext"
import { isBrowser } from "@utils/infrastructure/ssg-utils"
import { store } from "@utils/store/store"

type CityFilterRouteKind = "search" | "profile"

const getAmbiguousCityFilterKey = (pathname: string) => {
    const matched = matchPath({ path: "/psychologists/:city/:filter", end: true }, pathname)
    const city = matched?.params.city
    const filter = matched?.params.filter
    // Keep the `/psychologists/free/booking` route out of the ambiguous city/filter logic.
    if (!city || !filter || city === "free" || /^\d+$/.test(filter)) {
        return null
    }
    return `${city}/${filter}`
}

const getAmbiguousCityFilterStatus = (
    pathname: string,
    routeKindOverride?: CityFilterRouteKind
): APP_STATUSES | undefined | null => {
    const key = getAmbiguousCityFilterKey(pathname)
    if (!key) return null
    const routeKind = routeKindOverride ?? store.getState().psyCatalogMeta.cityFilterRouteKind[key]
    if (routeKind === "search") return APP_STATUSES.search_page
    if (routeKind === "profile") return APP_STATUSES.profile
    return undefined
}

export const getCurrentStatus = (
    pathname = isBrowser() ? location.pathname : "",
    routeKindOverride?: CityFilterRouteKind
) => {
    if (!isBrowser() && !pathname) return
    const ambiguousStatus = getAmbiguousCityFilterStatus(pathname, routeKindOverride)
    if (ambiguousStatus !== null) {
        return ambiguousStatus
    }
    const { searchParams } = new URL(location.href)
    const config = AnalyticStatusConfig.find(({ path }) =>
        matchPath({ path, end: true, caseSensitive: false }, pathname || location.pathname)
    )
    if (config) {
        if (typeof config.status === "function") {
            return config.status(searchParams) ?? APP_STATUSES.not_found_page
        }
        return config.status
    }
    return APP_STATUSES.not_found_page
}

interface SearchPagePathParams {
    city: string
    filter: string
    page: string
}

export function useSearchPageParams() {
    return useParams<Partial<SearchPagePathParams>>()
}

function matchCurrentPath(path: string, options?: { end?: boolean; caseSensitive?: boolean }) {
    if (!isBrowser()) return false
    return !!matchPath({ path, ...options }, window.location.pathname)
}

function matchUrlPath(
    pathname: string,
    path: string,
    options?: { end?: boolean; caseSensitive?: boolean }
) {
    return !!matchPath({ path, ...options }, pathname)
}

export function isAuthPage() {
    return matchCurrentPath("/my/authorization")
}

export function isClientAccountPage() {
    return matchCurrentPath("/my/*")
}

export function isClientAccountCardPage() {
    return matchCurrentPath("/my/cards")
}

export function isPsyAccountPage() {
    return matchCurrentPath("/psychologist-account/*")
}

export function isBookingPage() {
    return matchCurrentPath("/booking", { end: true })
}

export function isPaymentPage() {
    return matchCurrentPath(`/payment/:hash`, { end: true })
}

export function isSurveyPage() {
    return matchCurrentPath("/survey/psy", { end: true })
}

export function isSurveyResultsPage() {
    return matchCurrentPath("/results", { end: true })
}

export function isProfilePage() {
    if (!isBrowser()) return false
    const ambiguousStatus = getAmbiguousCityFilterStatus(window.location.pathname)
    if (ambiguousStatus !== null) {
        return ambiguousStatus === APP_STATUSES.profile
    }
    return (
        matchCurrentPath("/psychologist/:id", { end: true }) ||
        matchCurrentPath("/psychologists/:filter/:slug", { end: true })
    )
}

export function isListPage() {
    return (
        matchCurrentPath("/psychologist/list/:id", { end: true }) ||
        matchCurrentPath("/psychologist/list", { end: true })
    )
}

export function isSearchPage() {
    if (!isBrowser()) return false
    const ambiguousStatus = getAmbiguousCityFilterStatus(window.location.pathname)
    if (ambiguousStatus !== null) {
        return ambiguousStatus === APP_STATUSES.search_page
    }
    return (
        matchCurrentPath("/psychologists", { end: true }) ||
        matchCurrentPath("/psychologists/:page", { end: true }) ||
        matchCurrentPath("/psychologists/:city/:filter/:page", { end: true }) ||
        matchCurrentPath("/psychologists:filter(/[^/]+|)", { end: true }) ||
        matchCurrentPath("/psychologists:filter(/[^/]+|)/:page(\\d+)", { end: true })
    )
}

export function isClientAuthorizationPage() {
    return matchCurrentPath("/my/authorization", { end: true }) || isCompanyAuthPage()
}

export function isPsychologistCollectionPage() {
    return matchCurrentPath("/psychologist/collection/:id", { end: true })
}

export function isBookingDetailsPage() {
    return matchCurrentPath("/booking/:id", { end: true })
}

export function isVideoLobbyPage() {
    return (
        matchCurrentPath("/my/call/lobby/*") ||
        matchCurrentPath("/psychologist-account/call/*") ||
        matchCurrentPath("/internal/call/*")
    )
}

export function isClientNotificationUrl(pathName: string) {
    return !(
        matchUrlPath(pathName, "/my/call/*") || matchUrlPath(pathName, "/psychologist-account/*")
    )
}

export function isRootPage() {
    return matchCurrentPath("/")
}

export function isHRAccountPage() {
    return matchCurrentPath("/my/statistics")
}

export function isCompanyAuthPage() {
    return matchCurrentPath("/my/company/authorization")
}

export function isChat() {
    return matchCurrentPath("/chat")
}

export function isFavoritesPage() {
    return matchCurrentPath("/my/favorites", { end: true })
}
